// Implements: F-UIX-010
/**
 * The query keys of the application, declared once.
 *
 * One constant per server fact the application caches: the session bootstrap
 * and the device list (design section 4), the gallery and the trash, and the
 * version tree of a mockup (a function, because it is parametrised). The active
 * organization deliberately has no key of its own: it is derived from `["session","scopes"]` (DD9b), so invalidating
 * the session revalidates it without anything else having to remember.
 */

/** `GET /auth/me/scopes` — the session bootstrap, and the only profile call. */
export const SESSION_SCOPES_KEY = ["session", "scopes"] as const;

/** `GET /auth/sessions` — the list, invalidated when a session is revoked. */
export const AUTH_SESSIONS_KEY = ["auth", "sessions"] as const;

/** `GET /mockups` — the gallery pages, invalidated by every mutation of a card. */
export const MOCKUPS_KEY = ["mockups", "list"] as const;

/** `GET /mockups/trash` — the trash pages. */
export const MOCKUPS_TRASH_KEY = ["mockups", "trash"] as const;

export const queryKeys = {
  sessionScopes: SESSION_SCOPES_KEY,
  authSessions: AUTH_SESSIONS_KEY,
  mockups: MOCKUPS_KEY,
  mockupsTrash: MOCKUPS_TRASH_KEY,
} as const;

/**
 * The version tree of ONE mockup. A function and not a member of `queryKeys`
 * because it is parametrised, and so that anything that creates a version
 * (a finished generation) can invalidate exactly this mockup's tree.
 */
export function versionTreeKey(mockupId: string): readonly ["versions", "tree", string] {
  return ["versions", "tree", mockupId];
}

/** `GET /mockups/{id}` — one mockup. A function: it is parametrised. */
export function mockupDetailKey(mockupId: string): readonly ["mockups", "detail", string] {
  return ["mockups", "detail", mockupId];
}

/** `GET /mockups/{id}/versions/{version_id}` — one version. A function: it is parametrised. */
export function versionDetailKey(
  mockupId: string,
  versionId: string,
): readonly ["versions", "detail", string, string] {
  return ["versions", "detail", mockupId, versionId];
}

/** `GET /mockups/{id}/versions/diff` — one comparison, read-only and cacheable. */
export function versionDiffKey(
  mockupId: string,
  leftVersionId: string,
  rightVersionId: string,
): readonly ["versions", "diff", string, string, string] {
  return ["versions", "diff", mockupId, leftVersionId, rightVersionId];
}

/** `GET /mockups/{id}/conversation/messages` — the thread pages of ONE mockup. */
export function conversationMessagesKey(
  mockupId: string,
): readonly ["conversation", "messages", string] {
  return ["conversation", "messages", mockupId];
}

/** `GET /mockups/{id}/conversation` — who owns the mockup right now. */
export function conversationStateKey(
  mockupId: string,
): readonly ["conversation", "state", string] {
  return ["conversation", "state", mockupId];
}

/** Mockup v3 P4: `GET /mockups/{id}/assignments` — the people assigned to ONE mockup. */
export function mockupAssignmentsKey(mockupId: string): readonly ["mockups", "assignments", string] {
  return ["mockups", "assignments", mockupId];
}

/** Mockup v3 P3/P4: `GET /organizations/{org}/people` — the seats of ONE organization. */
export function organizationPeopleKey(organizationId: string): readonly ["organizations", "people", string] {
  return ["organizations", "people", organizationId];
}

/**
 * Mockup v3 P7: `GET /mockups/{id}/version-costs?version_id=…` — the costs of a SET of versions of
 * one mockup. The ids are sorted, so the same set asked in another order is the same cache entry.
 */
export function versionCostsKey(
  mockupId: string,
  versionIds: readonly string[],
): readonly ["mockups", string, "version-costs", readonly string[]] {
  return ["mockups", mockupId, "version-costs", [...versionIds].sort()];
}

/** Mockup v3 P7: `GET /admin/model-settings/catalogue` — the provider's model list (its params go after). */
export const MODEL_CATALOGUE_KEY = ["admin", "model-catalogue"] as const;

/** Mockup v3 P8: `GET /mockups/counts` — the size of each gallery tab (the scope goes after). */
export const GALLERY_COUNTS_KEY = ["mockups", "counts"] as const;

type OrganizationReportKey<Kind extends string> = readonly ["reports", "organizations", string, Kind, string, string];

/** Mockup v3 P9: `GET /reports/organizations/{org}/costs` — cost by model and flow over a day range. */
export function orgCostsKey(organizationId: string, from: string, to: string): OrganizationReportKey<"costs"> {
  return ["reports", "organizations", organizationId, "costs", from, to];
}

/** Mockup v3 P9: `GET /reports/organizations/{org}/activity` — the month's activity tiles. */
export function orgActivityKey(organizationId: string, from: string, to: string): OrganizationReportKey<"activity"> {
  return ["reports", "organizations", organizationId, "activity", from, to];
}

/** Mockup v3 P9: `GET /reports/organizations/{org}/latency` — latency and errors by model and step. */
export function orgLatencyKey(organizationId: string, from: string, to: string): OrganizationReportKey<"latency"> {
  return ["reports", "organizations", organizationId, "latency", from, to];
}

/** Mockup v3 P9: `GET /reports/platform/latency?organization_id=` — the same table, for the platform admin. */
export function platformLatencyKey(
  organizationId: string,
  from: string,
  to: string,
): readonly ["reports", "platform", "latency", string, string, string] {
  return ["reports", "platform", "latency", organizationId, from, to];
}

/** Mockup v3 P9: `GET /flows` — the generation flows and their step roles. */
export const FLOWS_KEY = ["flows"] as const;
